import React from "react";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";

const ALL_STATUSES = [
  "uploaded", "extracting_text", "ocring", "ready", "needs_ocr", "queued",
  "parsing", "parsed", "converted", "failed", "cancelled",
];

export const DEFAULT_STAGE = "non_candidates";

export const STAGES = [
  { key: "non_candidates", label: "Non-candidates", statuses: ALL_STATUSES.filter((status) => status !== "converted") },
  { key: "ready", label: "Ready", accent: "hsl(38 92% 50%)", statuses: ["ready", "failed", "cancelled", "needs_ocr"] },
  { key: "running", label: "Extracting", accent: "hsl(217 91% 55%)", statuses: ["queued", "parsing", "uploaded", "extracting_text", "ocring"] },
  { key: "parsed", label: "Extracted", accent: "hsl(142 71% 45%)", statuses: ["parsed"] },
  { key: "converted", label: "Candidates", accent: "hsl(150 38% 17%)", statuses: ["converted"] },
  { key: "scans", label: "Scans", accent: "hsl(38 92% 50%)", statuses: ["ocring", "needs_ocr"], derivedAccent: false },
];

export const STAGE_STATUSES = STAGES.reduce((acc, stage) => {
  acc[stage.key] = stage.statuses;
  return acc;
}, {});

const STATUS_ACCENT = STAGES.reduce((acc, stage) => {
  if (!stage.accent || stage.derivedAccent === false) return acc;

  stage.statuses.forEach((status) => {
    acc[status] = stage.accent;
  });
  return acc;
}, {});

export const statusAccent = (status, stalled) => {
  if (stalled || status === "failed") return "hsl(0 72% 51%)";
  if (status === "needs_ocr") return "hsl(38 92% 50%)";
  return STATUS_ACCENT[status] || "hsl(140 8% 78%)";
};

const Pill = ({ label, count, accent, active, onClick }) => (
  <Box
    component={motion.button}
    type="button"
    onClick={onClick}
    whileTap={{ scale: 0.97 }}
    sx={{
      all: "unset",
      boxSizing: "border-box",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      height: 30,
      px: 1.25,
      flexShrink: 0,
      borderRadius: "9999px",
      border: "1px solid",
      borderColor: active ? "hsl(150 38% 17%)" : "hsl(140 8% 88%)",
      backgroundColor: active ? "hsl(150 38% 17%)" : "#ffffff",
      transition: "background-color 140ms ease, border-color 140ms ease",
      "&:hover": { borderColor: active ? "hsl(150 38% 17%)" : "hsl(140 8% 74%)" },
      "&:focus-visible": { outline: "2px solid hsl(150 38% 17%)", outlineOffset: 2 },
    }}
  >
    {accent && (
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, backgroundColor: accent }} />
    )}
    <Typography
      sx={{
        fontSize: 12.5,
        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
        color: active ? "hsl(80 97% 72%)" : "hsl(150 30% 12%)",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 11.5,
        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
        color: active ? "hsla(80, 97%, 72%, 0.75)" : "hsl(150 10% 46%)",
      }}
    >
      {count}
    </Typography>
  </Box>
);

const VaultFilterBar = ({ stats, activeStage, onSelect }) => {
  const counts = stats || {};

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      <Pill
        label="All"
        count={counts.total || 0}
        active={!activeStage}
        onClick={() => onSelect(null)}
      />
      {STAGES.map((stage) => (
        <Pill
          key={stage.key}
          label={stage.label}
          accent={stage.accent}
          count={stage.statuses.reduce((sum, status) => sum + (counts[status] || 0), 0)}
          active={activeStage === stage.key}
          onClick={() => onSelect(activeStage === stage.key ? null : stage.key)}
        />
      ))}
    </Box>
  );
};

export default VaultFilterBar;
